import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CAMPAIGNS, type SubscriptionTier } from '@/api/subscriptions';
import { createCheckoutSession } from '@/api/polar';
import { useAuth } from '@/hooks';
import { useToast } from '@/hooks/use-toast';
import { PricingGrid } from './PricingGrid';

interface SubscriptionPaywallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: string;
}

export function SubscriptionPaywall({ open, onOpenChange, reason }: SubscriptionPaywallDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  // Track paywall view
  if (open && typeof window !== 'undefined' && window.plausible) {
    window.plausible('Paywall Viewed', {
      props: {
        reason: reason || 'unknown'
      }
    });
  }

  const handleSubscribe = async (tier: SubscriptionTier) => {
    // Track subscription attempt
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Subscription Attempt', {
        props: {
          tier: tier.id,
          from_paywall: 'true',
          reason: reason || 'unknown'
        }
      });
    }

    if (!user?.email) {
      toast({
        title: "Error",
        description: "Please sign in to subscribe.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Polar product IDs for all subscription tiers
      const tierProductMap: Record<SubscriptionTier['id'], string[]> = {
        solo: [
          "4ecdfad9-ed9f-464d-b66f-93ef90e3ac83", // Solo
          "814de567-163b-48a4-afae-4cb8a91d21ba", // Pro
        ],
        pro: [
          "814de567-163b-48a4-afae-4cb8a91d21ba", // Pro
          "4ecdfad9-ed9f-464d-b66f-93ef90e3ac83", // Solo
        ],
      };
      const productIds = tierProductMap[tier.id];

      // Get discount ID if campaign is set
      const discountId = tier.campaign ? CAMPAIGNS[tier.campaign]?.discount_id : undefined;

      const checkoutUrl = await createCheckoutSession(user.email, user.id, productIds, discountId);

      if (checkoutUrl) {
        // Track checkout opened
        if (typeof window !== 'undefined' && window.plausible) {
          window.plausible('Checkout Opened', {
            props: {
              tier: tier.id,
              has_discount: discountId ? 'true' : 'false'
            }
          });
        }

        window.open(checkoutUrl, '_blank');
        onOpenChange(false);
      } else {
        toast({
          title: "Error",
          description: "Unable to create checkout session. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open checkout. Please try again.",
        variant: "destructive",
      });
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] md:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Choose Your Plan
          </DialogTitle>
          {reason && (
            <DialogDescription className="text-center text-lg">
              {reason}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="mt-6">
          <PricingGrid
            recommendedTierId="solo"
            ctaText="Start Free Trial"
            onTierClick={handleSubscribe}
            useCTAButton={false}
          />
        </div>

        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>7-day free trial · Cancel anytime</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
